import type { IArticle, IArticleImage, IParagraph, ITextParagraph, ITitleParagraph } from '@pressreader/content-types';
import { localizedIssueDate } from '@pressreader/resources';
import { unhyphen } from '@pressreader/utils';

import type { ArticleTextViewModel } from '../../types';

import { findImageParagraphByImageId } from './find.image.paragraph.by.image.id';

interface IssueLike {
    title?: string;
    shortDateString?: string;
    date?: Date;
    newspaper?: { name?: string } | null;
    sectionName?: string;
    page?: { name?: string } | null;
}

const getImageScaleForWidth = (imgWidth: number, _imgHeight: number, requiredWidth: number, maxScale: number) => {
    if (requiredWidth > 0 && imgWidth > 0) {
        const scale = (requiredWidth * 100) / imgWidth;
        return Math.min(scale, maxScale);
    }
    return 0;
};

const getImageScaleForHeight = (_imgWidth: number, imgHeight: number, requiredHeight: number, maxScale: number) => {
    if (requiredHeight > 0 && imgHeight > 0) {
        const scale = (requiredHeight * 100) / imgHeight;
        return Math.min(scale, maxScale);
    }
    return 0;
};

function selectFirstImage(images: IArticleImage[] | null | undefined, a: IArticle): IArticleImage | null {
    if (!images?.length) return null;

    const firstImageWidth = 600;
    const imagesHeight = 120;
    const sizeScaleMin = 0.7;
    const sizeScaleMax = 2;
    const MaxImageScale = 400;

    let first: IArticleImage | null = null;

    for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const w = img.size?.width ?? 0;
        const h = img.size?.height ?? 0;

        if (w <= 10 || h <= 10) continue;
        if (h / w > 10 || w / h > 10) continue;

        const sizeScale = w / h;
        if (sizeScale <= sizeScaleMin || sizeScale >= sizeScaleMax) continue;

        const imageScale = getImageScaleForWidth(w, h, firstImageWidth, MaxImageScale);
        const imageScale2 = getImageScaleForHeight(w, h, imagesHeight, MaxImageScale);

        if (imageScale <= 0 || imageScale2 <= 0) continue;

        if (first == null) {
            first = img;
        }
        if ((img.size?.width ?? 0) > (first.size?.width ?? 0) || (img.size?.height ?? 0) > (first.size?.height ?? 0)) {
            first = img;
        }
    }

    return first ?? a.bestImage ?? a.coverImage ?? null;
}

const clean = (text: string | undefined | null) => unhyphen(text ?? '');

const isBodyText = (p: IParagraph): p is ITextParagraph => p.type === 'text';

const isTitleParagraph = (p: IParagraph): p is ITitleParagraph => p.type === 'title';

function hasBylineField(obj: unknown): obj is { byline?: string } {
    return !!obj && typeof obj === 'object' && 'byline' in obj;
}

function hasTextField(obj: unknown): obj is { text?: { text?: string } } {
    return !!obj && typeof obj === 'object' && 'text' in obj;
}

function hasTitleField(obj: unknown): obj is { title?: string } {
    return !!obj && typeof obj === 'object' && 'title' in obj;
}

export function createTextCopyArticleViewModel(a: IArticle, dir?: 'ltr' | 'rtl'): ArticleTextViewModel {
    const firstImage: IArticleImage | null = selectFirstImage(a.images ?? [], a);

    const blocksToStrings = (a.paragraphs ?? []).filter(isBodyText).map(p => clean(p.text ?? ''));

    const issue = a.issue as IssueLike | undefined;
    const issueTitle = issue?.title ?? issue?.newspaper?.name ?? '';

    let shortDateString = issue?.shortDateString;
    // If we have a Date, produce a localized short date string for legacy parity
    if (issue?.date) {
        shortDateString = localizedIssueDate(issue.date);
    }
    shortDateString = shortDateString ?? '';

    const sectionName = a.issue?.sectionName ?? '';
    const startPage = a.issue?.page?.name ?? '';
    const endPage = a.issue?.page?.name ?? '';

    const matchingImageParagraph = findImageParagraphByImageId(a.paragraphs, firstImage?.id);

    let imageTextValue = clean(matchingImageParagraph?.caption ?? '');
    if (!imageTextValue && firstImage && hasTextField(firstImage)) {
        imageTextValue = clean(firstImage.text?.text ?? '');
    }
    if (!imageTextValue) {
        imageTextValue = clean(firstImage?.caption ?? '');
    }

    let imageTitleValue = '';
    if (matchingImageParagraph) {
        const paragraphs = a.paragraphs ?? [];
        const idx = paragraphs.findIndex(p => p === matchingImageParagraph);
        if (idx >= 0) {
            if (paragraphs[idx - 1] && isTitleParagraph(paragraphs[idx - 1])) {
                imageTitleValue = clean((paragraphs[idx - 1] as ITitleParagraph).text ?? '');
            } else if (paragraphs[idx + 1] && isTitleParagraph(paragraphs[idx + 1])) {
                imageTitleValue = clean((paragraphs[idx + 1] as ITitleParagraph).text ?? '');
            }
        }
    }

    if (!imageTitleValue && firstImage && hasTitleField(firstImage)) {
        imageTitleValue = clean(firstImage.title ?? '');
    }

    let imageByline = '';
    if (firstImage && hasBylineField(firstImage)) {
        imageByline = clean(firstImage.byline);
    }

    return {
        articleTitle: clean(a.title ?? ''),
        articleSubtitle: clean(a.subtitle ?? ''),
        byline: clean(a.author ?? ''),
        articleBlocks: blocksToStrings,
        hasImage: !!firstImage,
        imageByline,
        imageUrl: firstImage?.url ?? '',
        imageTitle: imageTitleValue,
        imageText: imageTextValue,
        issueTitle: clean(issueTitle),
        shortDateString,
        sectionName: clean(sectionName),
        startPage: clean(startPage),
        endPage: clean(endPage),
        direction: dir ?? 'ltr',
    };
}
